import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  UserCheck,
  QrCode,
  TrendingUp,
  Plus,
  ArrowRight,
  Clock } from
'lucide-react';
import { Button } from '@/components/ui/button';
import { dashboardAPI, employeeAPI } from '@/lib/api';
import { format } from 'date-fns';

const StatCard = ({ icon: Icon, label, value, color, delay }) =>
<motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay }}
  className="stats-card">

    <div className="flex items-start justify-between !py-[24px] !px-[24px] !rounded-[25px] !bg-[#FFFFFF]">
      <div>
        <p className="text-slate-500 text-sm font-medium mb-1">{label}</p>
        <p className="text-3xl font-bold text-slate-900">{value}</p>
      </div>
      <div
      className="w-12 h-12 rounded-xl flex items-center justify-center"
      style={{ backgroundColor: `${color}15` }}>

        <Icon className="w-6 h-6" style={{ color }} />
      </div>
    </div>
  </motion.div>;


const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentEmployees, setRecentEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, employeesRes] = await Promise.all([
        dashboardAPI.getStats(),
        employeeAPI.list()]
        );
        setStats(statsRes.data);
        // Get 5 most recent employees
        const sorted = employeesRes.data.
        sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)).
        slice(0, 5);
        setRecentEmployees(sorted);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>);

  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-600 mt-1">Overview of your digital business cards</p>
        </div>
        <Link to="/admin/employees/new">
          <Button className="btn-primary" data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard
          icon={Users}
          label="Total Employees"
          value={stats?.total_employees || 0}
          color="#0F4C81"
          delay={0.1} />

        <StatCard
          icon={UserCheck}
          label="Active Cards"
          value={stats?.active_employees || 0}
          color="#10B981"
          delay={0.2} />

        <StatCard
          icon={QrCode}
          label="Total Scans"
          value={stats?.total_scans || 0}
          color="#00A3C4"
          delay={0.3} />

        <StatCard
          icon={TrendingUp}
          label="Scans Today"
          value={stats?.scans_today || 0}
          color="#F59E0B"
          delay={0.4} />

      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Employees */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl border border-slate-100 overflow-hidden">

          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Recent Employees</h2>
            <Link to="/admin/employees" className="text-[#0F4C81] text-sm font-medium hover:underline flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-slate-50">
            {recentEmployees.length === 0 ?
            <div className="p-6 text-center text-slate-500">
                No employees yet. Add your first employee card!
              </div> :

            recentEmployees.map((employee) =>
            <Link
              key={employee.id}
              to={`/admin/employees/${employee.id}/edit`}
              className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
              data-testid={`employee-row-${employee.id}`}>

                  <div className="w-10 h-10 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                    {employee.profile_photo ?
                <img
                  src={employee.profile_photo}
                  alt={employee.full_name}
                  className="w-full h-full object-cover" /> :


                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0F4C81] to-[#00A3C4] text-white font-semibold text-sm">
                        {employee.full_name.split(' ').map((n) => n[0]).join('')}
                      </div>
                }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{employee.full_name}</p>
                    <p className="text-sm text-slate-500 truncate">{employee.job_title}</p>
                  </div>
                  <span className={`status-badge ${employee.is_active ? 'active' : 'inactive'}`}>
                    {employee.is_active ? 'Active' : 'Inactive'}
                  </span>
                </Link>
            )
            }
          </div>
        </motion.div>

        {/* Recent Scans */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl border border-slate-100 overflow-hidden">

          <div className="p-6 border-b border-slate-100">
            <h2 className="font-semibold text-slate-900">Recent QR Scans</h2>
          </div>
          <div className="divide-y divide-slate-50">
            {!stats?.recent_scans || stats.recent_scans.length === 0 ?
            <div className="p-6 text-center text-slate-500">
                No scans recorded yet. Share your QR codes!
              </div> :

            stats.recent_scans.slice(0, 5).map((scan, index) =>
            <div
              key={scan.id || index}
              className="flex items-center gap-4 p-4">

                  <div className="w-10 h-10 rounded-xl bg-[#00A3C4]/10 flex items-center justify-center flex-shrink-0">
                    <QrCode className="w-5 h-5 text-[#00A3C4]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">
                      {scan.employee_name || 'Unknown'}
                    </p>
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span>
                        {scan.timestamp ?
                    format(new Date(scan.timestamp), 'MMM d, yyyy h:mm a') :
                    'Unknown time'
                    }
                      </span>
                    </div>
                  </div>
                </div>
            )
            }
          </div>
        </motion.div>
      </div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-gradient-to-br from-[#0F4C81] to-[#0c3b66] rounded-2xl p-6 text-white">

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-lg">Weekly Performance</h3>
            <p className="text-white/70 mt-1">
              {stats?.scans_this_week || 0} QR code scans in the last 7 days
            </p>
          </div>
          <div className="flex items-center gap-2 text-white/90">
            <TrendingUp className="w-5 h-5" />
            <span className="font-semibold">
              {stats?.active_employees || 0} active cards generating traffic
            </span>
          </div>
        </div>
      </motion.div>
    </div>);

};

export default AdminDashboard;